/**
 * DOM操作工具集
 * 整合DOM操作、安全操作、元素查找、样式计算等功能
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 2.0.0
 */

import { nextTick, onBeforeUnmount } from 'vue';

// ============= 类型定义 =============

export interface ElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export interface StyleObject {
  [key: string]: string | number;
}

export interface ScrollPosition {
  x: number;
  y: number;
}

// ============= DOM安全操作 =============

/**
 * 安全的DOM操作包装器
 * @param operation DOM操作函数
 * @param fallback 失败时的回调
 */
export function safeDOMOperation<T>(
  operation: () => T,
  fallback?: () => T
): T | undefined {
  try {
    return operation();
  } catch (error) {
    if (error instanceof Error && error.message.includes('parentNode')) {
      return fallback?.();
    }
    // 其他错误继续抛出
    throw error;
  }
}

/**
 * 安全的异步DOM操作
 * @param operation 异步DOM操作函数
 * @param fallback 失败时的回调
 */
export async function safeAsyncDOMOperation<T>(
  operation: () => Promise<T>,
  fallback?: () => Promise<T> | T
): Promise<T | undefined> {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof Error && error.message.includes('parentNode')) {
      return fallback ? await fallback() : undefined;
    }
    // 其他错误继续抛出
    throw error;
  }
}

/**
 * 安全的nextTick操作
 * @param callback 回调函数
 */
export function safeNextTick(callback: () => void): void {
  nextTick(() => {
    safeDOMOperation(callback);
  });
}

/**
 * 检查元素是否仍然在DOM中
 * @param element DOM元素
 */
export function isElementInDOM(element: Element | null): boolean {
  if (!element) return false;

  try {
    return document.contains(element);
  } catch (error) {
    return false;
  }
}

/**
 * 安全的路由跳转
 * @param router Vue Router实例
 * @param path 目标路径
 */
export async function safeRouterPush(
  router: any,
  path: string
): Promise<boolean> {
  try {
    await router.push(path);
    return true;
  } catch (error) {
    if (error instanceof Error && error.message.includes('parentNode')) {
      // 静默处理DOM错误，延迟重试
      setTimeout(() => {
        router.push(path).catch(() => {
          // 静默处理重试失败
        });
      }, 100);
      return false;
    }
    // 其他错误继续抛出
    throw error;
  }
}

// ============= 元素查找 =============

/**
 * 安全查找元素
 * @param selector CSS选择器
 * @param context 查找上下文，默认为document
 */
export function safeQuerySelector<T extends Element = Element>(
  selector: string,
  context: Document | Element = document
): T | null {
  return safeDOMOperation(() => context.querySelector<T>(selector)) || null;
}

/**
 * 安全查找多个元素
 * @param selector CSS选择器
 * @param context 查找上下文，默认为document
 */
export function safeQuerySelectorAll<T extends Element = Element>(
  selector: string,
  context: Document | Element = document
): T[] {
  return safeDOMOperation(() => Array.from(context.querySelectorAll<T>(selector))) || [];
}

/**
 * 查找最近的匹配祖先元素
 * @param element 起始元素
 * @param selector CSS选择器
 */
export function findClosest<T extends Element = Element>(
  element: Element,
  selector: string
): T | null {
  return safeDOMOperation(() => element.closest<T>(selector)) || null;
}

/**
 * 查找元素的所有子元素
 * @param element 父元素
 * @param selector CSS选择器（可选）
 */
export function findChildren<T extends Element = Element>(
  element: Element,
  selector?: string
): T[] {
  return safeDOMOperation(() => {
    if (selector) {
      return Array.from(element.querySelectorAll<T>(selector));
    }
    return Array.from(element.children) as T[];
  }) || [];
}

// ============= 位置和尺寸计算 =============

/**
 * 获取元素位置信息
 * @param element DOM元素
 */
export function getElementPosition(element: Element): ElementPosition | null {
  return safeDOMOperation(() => {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      right: rect.right,
      bottom: rect.bottom
    };
  }) || null;
}

/**
 * 获取元素相对于页面的偏移位置
 * @param element DOM元素
 */
export function getElementOffsetPosition(element: Element): ElementPosition | null {
  return safeDOMOperation(() => {
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft,
      width: rect.width,
      height: rect.height,
      right: rect.right + scrollLeft,
      bottom: rect.bottom + scrollTop
    };
  }) || null;
}

/**
 * 获取滚动位置
 * @param element 元素，默认为window
 */
export function getScrollPosition(element?: Element): ScrollPosition {
  return safeDOMOperation(() => {
    if (element) {
      return {
        x: element.scrollLeft,
        y: element.scrollTop
      };
    }
    return {
      x: window.pageXOffset || document.documentElement.scrollLeft,
      y: window.pageYOffset || document.documentElement.scrollTop
    };
  }) || { x: 0, y: 0 };
}

/**
 * 设置滚动位置
 * @param position 滚动位置
 * @param element 元素，默认为window
 * @param smooth 是否平滑滚动
 */
export function setScrollPosition(
  position: Partial<ScrollPosition>,
  element?: Element,
  smooth = false
): void {
  safeDOMOperation(() => {
    const behavior = smooth ? 'smooth' : 'auto';
    
    if (element) {
      element.scrollTo({
        left: position.x,
        top: position.y,
        behavior
      });
    } else {
      window.scrollTo({
        left: position.x,
        top: position.y,
        behavior
      });
    }
  });
}

// ============= 样式操作 =============

/**
 * 安全获取计算样式
 * @param element DOM元素
 * @param property CSS属性名（可选）
 */
export function getComputedStyle(
  element: Element,
  property?: string
): string | CSSStyleDeclaration | null {
  return safeDOMOperation(() => {
    const computed = window.getComputedStyle(element);
    return property ? computed.getPropertyValue(property) : computed;
  }) || null;
}

/**
 * 安全设置样式
 * @param element DOM元素
 * @param styles 样式对象
 */
export function setStyles(element: HTMLElement, styles: StyleObject): void {
  safeDOMOperation(() => {
    Object.entries(styles).forEach(([property, value]) => {
      element.style.setProperty(property, String(value));
    });
  });
}

/**
 * 安全添加CSS类
 * @param element DOM元素
 * @param classNames 类名
 */
export function addClass(element: Element, ...classNames: string[]): void {
  safeDOMOperation(() => {
    element.classList.add(...classNames);
  });
}

/**
 * 安全移除CSS类
 * @param element DOM元素
 * @param classNames 类名
 */
export function removeClass(element: Element, ...classNames: string[]): void {
  safeDOMOperation(() => {
    element.classList.remove(...classNames);
  });
}

/**
 * 安全切换CSS类
 * @param element DOM元素
 * @param className 类名
 * @param force 强制添加或移除
 */
export function toggleClass(element: Element, className: string, force?: boolean): boolean {
  return safeDOMOperation(() => {
    return element.classList.toggle(className, force);
  }) || false;
}

/**
 * 检查元素是否包含CSS类
 * @param element DOM元素
 * @param className 类名
 */
export function hasClass(element: Element, className: string): boolean {
  return safeDOMOperation(() => {
    return element.classList.contains(className);
  }) || false;
}

// ============= 事件处理 =============

/**
 * 安全添加事件监听器
 * @param element DOM元素
 * @param event 事件名
 * @param handler 事件处理函数
 * @param options 事件选项
 */
export function addEventListener<K extends keyof HTMLElementEventMap>(
  element: Element,
  event: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  options?: boolean | AddEventListenerOptions
): () => void {
  safeDOMOperation(() => {
    element.addEventListener(event, handler as EventListener, options);
  });

  // 返回移除监听器的函数
  return () => {
    safeDOMOperation(() => {
      element.removeEventListener(event, handler as EventListener, options);
    });
  };
}

/**
 * 防抖事件处理器
 * @param handler 事件处理函数
 * @param delay 延迟时间（毫秒）
 */
export function debounceEventHandler<T extends (...args: any[]) => void>(
  handler: T,
  delay: number
): T {
  let timeoutId: number;

  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => handler(...args), delay);
  }) as T;
}

/**
 * 节流事件处理器
 * @param handler 事件处理函数
 * @param delay 延迟时间（毫秒）
 */
export function throttleEventHandler<T extends (...args: any[]) => void>(
  handler: T,
  delay: number
): T {
  let lastCall = 0;

  return ((...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      handler(...args);
    }
  }) as T;
}

// ============= 组件安全操作 =============

/**
 * 组件卸载状态检查器
 */
export class ComponentUnmountChecker {
  private isUnmounted = false;

  /**
   * 标记组件已卸载
   */
  markUnmounted(): void {
    this.isUnmounted = true;
  }

  /**
   * 检查组件是否已卸载
   */
  isComponentUnmounted(): boolean {
    return this.isUnmounted;
  }

  /**
   * 安全执行操作（仅在组件未卸载时）
   */
  safeExecute<T>(operation: () => T): T | undefined {
    if (this.isUnmounted) {
      return undefined;
    }

    return safeDOMOperation(operation);
  }

  /**
   * 安全执行异步操作（仅在组件未卸载时）
   */
  async safeExecuteAsync<T>(operation: () => Promise<T>): Promise<T | undefined> {
    if (this.isUnmounted) {
      return undefined;
    }

    return safeAsyncDOMOperation(operation);
  }
}

/**
 * 创建组件安全操作钩子
 * 在组件setup中使用，自动处理组件卸载状态
 */
export function useComponentSafety() {
  const checker = new ComponentUnmountChecker();

  // 在组件卸载时自动标记
  onBeforeUnmount(() => {
    checker.markUnmounted();
  });

  return {
    safeExecute: checker.safeExecute.bind(checker),
    safeExecuteAsync: checker.safeExecuteAsync.bind(checker),
    isUnmounted: () => checker.isComponentUnmounted()
  };
}

// ============= 全局错误处理 =============

/**
 * 全局错误处理器设置
 */
export function setupGlobalDOMErrorHandling(): void {
  // 捕获未处理的Promise错误
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason instanceof Error && event.reason.message.includes('parentNode')) {
      event.preventDefault(); // 静默处理DOM访问错误
    }
  });

  // 捕获全局错误
  window.addEventListener('error', (event) => {
    if (event.error instanceof Error && event.error.message.includes('parentNode')) {
      event.preventDefault(); // 静默处理DOM访问错误
    }
  });
}

// ============= 工具函数 =============

/**
 * 检查元素是否可见
 * @param element DOM元素
 */
export function isElementVisible(element: Element): boolean {
  return safeDOMOperation(() => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }) || false;
}

/**
 * 检查元素是否在视口中
 * @param element DOM元素
 * @param threshold 阈值（0-1）
 */
export function isElementInViewport(element: Element, threshold = 0): boolean {
  return safeDOMOperation(() => {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    const verticalThreshold = windowHeight * threshold;
    const horizontalThreshold = windowWidth * threshold;

    return (
      rect.top >= -verticalThreshold &&
      rect.left >= -horizontalThreshold &&
      rect.bottom <= windowHeight + verticalThreshold &&
      rect.right <= windowWidth + horizontalThreshold
    );
  }) || false;
}

/**
 * 滚动元素到视口中
 * @param element DOM元素
 * @param options 滚动选项
 */
export function scrollIntoView(
  element: Element,
  options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' }
): void {
  safeDOMOperation(() => {
    element.scrollIntoView(options);
  });
}
